'use client';

import React, { useEffect, useRef } from 'react';
import { MenuSection, MenuItem } from '@/types';
import Header from './menu-sections/Header';
import Footer from './menu-sections/Footer';
import { NavigationHeader, BrandFooter } from '@/components/navigation';
import { usePreview } from '@/contexts/PreviewContext';
import { PublicFormData, PublicPolicyData } from '@/lib/public-site-data';

interface MenuSectionRendererProps {
  menuSection: MenuSection;
  menuItems?: MenuItem[];
  footerMenuSection?: MenuSection;
  subscriptionForm?: PublicFormData | null;
  policies?: PublicPolicyData[];
  useComponentized?: boolean; // Option to use new componentized components
}

// Component to handle header spacing for sections
function HeaderSpacer({ headerElement, wrapperProps }: { headerElement: React.ReactNode; wrapperProps?: React.HTMLAttributes<HTMLDivElement> }) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // No-op hook now that we rely on the layout itself rather than forcing padding on <main>
    return () => undefined;
  }, []);

  return (
    <div ref={wrapperRef} {...wrapperProps}>
      {headerElement}
    </div>
  );
}

export default function MenuSectionRenderer({ 
  menuSection, 
  menuItems,
  footerMenuSection,
  subscriptionForm = null,
  policies = [],
  useComponentized = false // Default to using responsive Header/Footer components
}: MenuSectionRendererProps) {
  const preview = usePreview();
  const menuSectionWrapperProps: React.HTMLAttributes<HTMLDivElement> & {
    'data-menu-section-id'?: string;
    'data-menu-section-type'?: string;
  } = preview?.isPreviewMode
    ? {
        'data-menu-section-id': menuSection?.id ?? '',
        'data-menu-section-type': menuSection?.type ?? '',
      }
    : {};

  if (!menuSection || !menuSection.isActive) {
    return null;
  }

  // Use componentized components if enabled
  if (useComponentized) {
    switch (menuSection.type) {
      case 'HEADER':
        return (
          <HeaderSpacer
            headerElement={
              <NavigationHeader
                logo={menuSection.content?.logo as string}
                logoAlt={menuSection.content?.logoAlt as string || `${menuSection.name} Logo`}
                links={menuSection.content?.links as Array<{ label: string; url: string; isExternal?: boolean }> || (menuItems || []).map(item => ({
                  label: item.label,
                  url: item.url,
                  isExternal: item.isExternal
                }))}
              />
            }
            wrapperProps={menuSectionWrapperProps}
          />
        );
      case 'FOOTER':
        // Support both linkGroups and links for backward compatibility
        const footerLinkGroups = menuSection.content?.linkGroups as Array<{ links: MenuItem[] }> || [];
        const footerLinks = menuSection.content?.links as MenuItem[] || [];
        // Convert MenuItem[] to LinkGroup format
        const linkGroups = footerLinkGroups.length > 0
          ? footerLinkGroups.map(group => ({
              links: group.links
                .filter(item => item.isActive)
                .sort((a, b) => a.order - b.order)
                .map(item => ({
                  label: item.label,
                  url: item.url,
                  isExternal: item.isExternal || false
                }))
            }))
          : footerLinks.length > 0 ? [{
              links: footerLinks
                .filter(item => item.isActive)
                .sort((a, b) => a.order - b.order)
                .map(item => ({
                  label: item.label,
                  url: item.url,
                  isExternal: item.isExternal || false
                }))
            }] : [];
        return (
          <BrandFooter
            copyright={menuSection.content?.copyright as string}
            linkGroups={linkGroups}
            content={menuSection.content?.content as string | undefined}
          />
          );
      default:
        return null;
    }
  }

  // Fall back to original components
  switch (menuSection.type) {
    case 'HEADER':
      return (
        <HeaderSpacer
          headerElement={
            <Header
              menuSection={menuSection}
              menuItems={menuItems}
              footerMenuSection={footerMenuSection}
              subscriptionForm={subscriptionForm}
            />
          }
          wrapperProps={menuSectionWrapperProps}
        />
      );
    case 'FOOTER':
      return (
        <div {...menuSectionWrapperProps} style={{ backgroundColor: '#ffffff' }} className="bg-white">
          <Footer
            menuSection={menuSection}
            menuItems={menuItems}
            subscriptionForm={subscriptionForm}
            policies={policies}
          />
        </div>
      );
    default:
      return null;
  }
}
